/**
 * CampaignsList - Platform nurture campaigns list page
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { cn, Button, Input } from '@respectly/ui';
import {
  Plus,
  Search,
  Zap,
  RefreshCw,
  UserPlus,
  Clock,
  ArrowUpCircle,
  Moon,
  Hand,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  useCampaignsStore,
  useActiveCampaignsCount,
  useTotalEnrollmentCount,
} from '../../stores/useCampaignsStore';
import { CampaignCard } from '../../components/campaigns';
import type { PlatformCampaign, PlatformCampaignStatus, CampaignTrigger } from '@respectly/shared-types';

// ============================================
// Filter Config
// ============================================

const statusFilters: { value: PlatformCampaignStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

const triggerFilters: { value: CampaignTrigger | 'all'; label: string; icon: typeof UserPlus }[] = [
  { value: 'all', label: 'All Triggers', icon: Zap },
  { value: 'signup', label: 'Signup', icon: UserPlus },
  { value: 'trial_end', label: 'Trial End', icon: Clock },
  { value: 'plan_change', label: 'Plan Change', icon: ArrowUpCircle },
  { value: 'inactivity', label: 'Inactivity', icon: Moon },
  { value: 'manual', label: 'Manual', icon: Hand },
];

// ============================================
// Component
// ============================================

export function CampaignsList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<PlatformCampaignStatus | 'all'>('all');
  const [triggerFilter, setTriggerFilter] = useState<CampaignTrigger | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const {
    campaigns,
    isLoadingList,
    loadCampaigns,
    deleteCampaign,
    activateCampaign,
    pauseCampaign,
  } = useCampaignsStore();

  const activeCampaignsCount = useActiveCampaignsCount();
  const totalEnrollments = useTotalEnrollmentCount();

  const avgCompletionRate =
    campaigns.length > 0
      ? Math.round(
          campaigns.reduce((sum, c) =>
            sum + (c.enrolledCount > 0 ? (c.completedCount / c.enrolledCount) * 100 : 0), 0
          ) / campaigns.length
        )
      : 0;

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const filteredCampaigns = campaigns.filter((campaign) => {
    if (statusFilter !== 'all' && campaign.status !== statusFilter) return false;
    if (triggerFilter !== 'all' && campaign.trigger !== triggerFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return campaign.name.toLowerCase().includes(q) || campaign.description?.toLowerCase().includes(q);
    }
    return true;
  });

  const handleCreateCampaign = () => navigate({ to: '/campaigns/new' });
  const handleViewCampaign = (campaign: PlatformCampaign) =>
    navigate({ to: '/campaigns/$id', params: { id: campaign.id } });
  const handleEditCampaign = (campaign: PlatformCampaign) =>
    navigate({ to: '/campaigns/$id/edit', params: { id: campaign.id } });
  const handleDeleteCampaign = async (campaign: PlatformCampaign) => {
    if (confirm(`Are you sure you want to delete "${campaign.name}"?`)) {
      await deleteCampaign(campaign.id);
    }
  };
  const handleActivateCampaign = async (campaign: PlatformCampaign) => activateCampaign(campaign.id);
  const handlePauseCampaign = async (campaign: PlatformCampaign) => pauseCampaign(campaign.id);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col h-full"
    >
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between px-4 lg:px-6 pt-4 lg:pt-6 pb-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              Platform Campaigns
            </h1>
            <p className="mt-1 text-sm text-slate-500">B2B nurture sequences for tenant accounts</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadCampaigns()}
              disabled={isLoadingList}
              className="h-9 w-9 p-0"
            >
              <RefreshCw className={cn('h-4 w-4', isLoadingList && 'animate-spin')} />
            </Button>
            <Button onClick={handleCreateCampaign} size="sm">
              <Plus className="h-4 w-4 mr-1.5" />
              New Campaign
            </Button>
          </div>
        </div>

        {/* Stats Strip */}
        {campaigns.length > 0 && (
          <div className="px-4 lg:px-6 pb-3">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-semibold text-slate-900 dark:text-white tabular-nums">{activeCampaignsCount}</span>
                <span className="text-slate-500">active</span>
              </div>
              <div className="h-3 w-px bg-slate-200 dark:bg-slate-700" />
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-slate-400" />
                <span className="font-semibold text-slate-900 dark:text-white tabular-nums">{totalEnrollments.toLocaleString()}</span>
                <span className="text-slate-500">enrolled</span>
              </div>
              <div className="h-3 w-px bg-slate-200 dark:bg-slate-700" />
              <div className="flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-slate-400" />
                <span className="font-semibold text-slate-900 dark:text-white tabular-nums">{avgCompletionRate}%</span>
                <span className="text-slate-500">avg completion</span>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="px-4 lg:px-6 pb-4 space-y-3">
          {/* Status Tabs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1 flex-wrap">
              {statusFilters.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setStatusFilter(filter.value)}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap',
                    statusFilter === filter.value
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  )}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>

          {/* Trigger Pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {triggerFilters.map((filter) => {
              const Icon = filter.icon;
              const isActive = triggerFilter === filter.value;
              return (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setTriggerFilter(filter.value)}
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all',
                    isActive
                      ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        {isLoadingList ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-52 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
            ))}
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="rounded-2xl bg-slate-100 dark:bg-slate-800 p-4 mb-4">
              <Zap className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {searchQuery || statusFilter !== 'all' || triggerFilter !== 'all'
                ? 'No matching campaigns'
                : 'No campaigns yet'}
            </h3>
            <p className="text-sm text-slate-500 mb-6 max-w-sm">
              {searchQuery || statusFilter !== 'all' || triggerFilter !== 'all'
                ? 'Try adjusting your filters or search query.'
                : 'Create automated nurture sequences that engage your tenant accounts at the right moment.'}
            </p>
            {!searchQuery && statusFilter === 'all' && triggerFilter === 'all' && (
              <Button onClick={handleCreateCampaign}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Campaign
              </Button>
            )}
          </div>
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.04 } },
            }}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {filteredCampaigns.map((campaign) => (
              <motion.div
                key={campaign.id}
                variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.2 }}
              >
                <CampaignCard
                  campaign={campaign}
                  onView={() => handleViewCampaign(campaign)}
                  onEdit={() => handleEditCampaign(campaign)}
                  onDelete={() => handleDeleteCampaign(campaign)}
                  onActivate={() => handleActivateCampaign(campaign)}
                  onPause={() => handlePauseCampaign(campaign)}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
